import { useState, useEffect, useCallback, useMemo } from 'react';

import { useDataContext } from '../context/DataContext.tsx';
import { useSurfaceContext } from '../context/SurfaceContext.tsx';
import { getBindingCandidatePaths, resolveBindingValue } from '../engine/dynamicResolver.ts';

export function useDataBinding<T = unknown>(path: string): [T | undefined, (value: T) => void] {
	const { state } = useSurfaceContext();
	const { basePath } = useDataContext();
	const { dataModel } = state;

	const candidatePaths = useMemo(() => getBindingCandidatePaths(basePath, path), [basePath, path]);
	const resolvedPath = candidatePaths[0];

	const [value, setValue] = useState<T | undefined>(
		() => resolveBindingValue(dataModel, basePath, path) as T | undefined,
	);

	useEffect(() => {
		setValue(resolveBindingValue(dataModel, basePath, path) as T | undefined);
		const unsubscribes = candidatePaths.map((candidatePath) =>
			dataModel.subscribe(candidatePath, () => {
				setValue(resolveBindingValue(dataModel, basePath, path) as T | undefined);
			}),
		);
		return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
	}, [dataModel, basePath, path, candidatePaths]);

	const setDataValue = useCallback(
		(newValue: T) => {
			dataModel.set(resolvedPath, newValue);
		},
		[dataModel, resolvedPath],
	);

	return [value, setDataValue];
}
